import { getOperatorReadinessSummary } from '@repo/types'
import { useTheme } from '@repo/ui/components/theme-provider'
import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Link } from '@tanstack/react-router'
import type { Feature, FeatureCollection, Geometry, Point } from 'geojson'
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Clock3,
  MapPinned,
  MoveRight,
  Navigation,
  Plus,
  ShieldCheck,
} from 'lucide-react'
import maplibregl, { type GeoJSONSource, type MapLayerMouseEvent } from 'maplibre-gl'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AppWorkspaceHeader,
  AppWorkspacePanel,
  WorkspaceMetricStrip,
} from '@/components/app/workspace-shell'
import {
  createMap,
  createMapStyle,
  escapeMapHtml,
  fitMapToPoints,
  invalidateMapSize,
  type LngLat,
  latLngToLngLat,
  syncMapWhenStyleIsReady,
} from '@/lib/maplibre'
import { useOperatorConfiguration } from '@/lib/operator-config/use-operator-configuration'
import { trpc } from '@/lib/trpc'

type OperatorOverviewMode = 'home' | 'schedule'

type Appointment = {
  id: string
  date: string
  startTime: string
  endTime: string
  durationMinutes: number
  status: string
  addressText: string | null
  h3Index?: string | null
  lat?: string | null
  lng?: string | null
  isFlexible: boolean
  customer?: { name: string; phone: string | null } | null
  service?: { name: string } | null
}

type ViewerLocation = {
  lat: number
  lng: number
  city: string | null
  region: string | null
  country: string | null
} | null

function DashboardRouteMap({
  appointments,
  viewerLocation,
}: {
  appointments: Appointment[]
  viewerLocation: ViewerLocation
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const popupRef = useRef<maplibregl.Popup | null>(null)
  const [mapError, setMapError] = useState(false)
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  const appointmentPoints = useMemo(
    () =>
      appointments
        .filter((appointment) => appointment.lat && appointment.lng)
        .sort((left, right) => left.startTime.localeCompare(right.startTime))
        .map((appointment) => ({
          appointment,
          position: [Number(appointment.lat), Number(appointment.lng)] as [number, number],
        }))
        .filter(({ position }) => !Number.isNaN(position[0]) && !Number.isNaN(position[1])),
    [appointments]
  )

  const viewerPosition = useMemo(
    () => (viewerLocation ? ([viewerLocation.lat, viewerLocation.lng] as [number, number]) : null),
    [viewerLocation]
  )
  const positions = useMemo(
    () => [
      ...(viewerPosition ? [viewerPosition] : []),
      ...appointmentPoints.map((point) => point.position),
    ],
    [appointmentPoints, viewerPosition]
  )
  const hasPositions = positions.length > 0
  const mapPoints = useMemo<LngLat[]>(
    () => positions.map((position) => latLngToLngLat(position)),
    [positions]
  )
  const routeData = useMemo<FeatureCollection<Point, RoutePointProperties>>(
    () => ({
      type: 'FeatureCollection',
      features: [
        ...(viewerPosition
          ? [
              {
                type: 'Feature' as const,
                geometry: { type: 'Point' as const, coordinates: latLngToLngLat(viewerPosition) },
                properties: {
                  kind: 'current' as const,
                  title: 'Current location',
                  detail: [viewerLocation?.city, viewerLocation?.region].filter(Boolean).join(', '),
                  secondary: '',
                  addressText: '',
                },
              },
            ]
          : []),
        ...appointmentPoints.map(({ appointment, position }, index) => ({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: latLngToLngLat(position) },
          properties: {
            kind: 'appointment' as const,
            title: `${index + 1}. ${appointment.service?.name || 'Appointment'}`,
            detail: `${appointment.startTime} - ${appointment.endTime}`,
            secondary: appointment.customer?.name ?? '',
            addressText: appointment.addressText ?? '',
          },
        })),
      ],
    }),
    [appointmentPoints, viewerLocation?.city, viewerLocation?.region, viewerPosition]
  )

  useEffect(() => {
    const container = containerRef.current
    if (!hasPositions || !container || mapRef.current) return

    let map: maplibregl.Map
    try {
      map = createMap({
        container,
        center: [39.8283, -98.5795],
        zoom: 11,
        isDark,
        scrollZoom: false,
      })
    } catch {
      setMapError(true)
      return
    }

    setMapError(false)
    mapRef.current = map
    map.on('load', () => {
      addRouteSourcesAndLayers(map, isDark)
      setRouteSourceData(map, routeData)
      fitMapToPoints({
        map,
        points: mapPoints,
        fallbackCenter: [39.8283, -98.5795],
        fallbackZoom: 11,
        maxZoom: 13,
        padding: 36,
      })
    })

    return () => {
      popupRef.current?.remove()
      popupRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, [hasPositions, isDark, mapPoints, routeData])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    map.setStyle(createMapStyle(isDark))
  }, [isDark])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    return syncMapWhenStyleIsReady(map, () => {
      addRouteSourcesAndLayers(map, isDark)
      setRouteSourceData(map, routeData)
    })
  }, [isDark, routeData])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const handleClick = (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0] as Feature<Point, RoutePointProperties> | undefined
      if (!feature) return
      popupRef.current?.remove()
      popupRef.current = new maplibregl.Popup({ closeButton: false, maxWidth: '240px' })
        .setLngLat(event.lngLat)
        .setHTML(renderRoutePopup(feature.properties))
        .addTo(map)
    }
    const handleEnter = () => {
      map.getCanvas().style.cursor = 'pointer'
    }
    const handleLeave = () => {
      map.getCanvas().style.cursor = ''
    }

    map.on('click', 'route-point', handleClick)
    map.on('mouseenter', 'route-point', handleEnter)
    map.on('mouseleave', 'route-point', handleLeave)
    return () => {
      map.off('click', 'route-point', handleClick)
      map.off('mouseenter', 'route-point', handleEnter)
      map.off('mouseleave', 'route-point', handleLeave)
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    setRouteSourceData(map, routeData)
    fitMapToPoints({
      map,
      points: mapPoints,
      fallbackCenter: [39.8283, -98.5795],
      fallbackZoom: 11,
      maxZoom: 13,
      padding: 36,
    })
  }, [mapPoints, routeData])

  useEffect(() => {
    const map = mapRef.current
    const container = containerRef.current
    if (!hasPositions || !map || !container) return

    const handleResize = () => invalidateMapSize(map)
    const frame = window.requestAnimationFrame(handleResize)
    window.addEventListener('resize', handleResize)

    const resizeObserver = new ResizeObserver(handleResize)
    resizeObserver.observe(container)

    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', handleResize)
      resizeObserver.disconnect()
    }
  }, [hasPositions])

  if (!hasPositions || mapError) {
    const hasAppointments = appointments.length > 0

    return (
      <div className="app-data-card relative flex h-full min-h-64 overflow-hidden rounded-[4px] px-6 text-center">
        <div className="absolute inset-0 opacity-70 [background-image:linear-gradient(color-mix(in_oklab,var(--border)_55%,transparent)_1px,transparent_1px),linear-gradient(90deg,color-mix(in_oklab,var(--border)_55%,transparent)_1px,transparent_1px)] [background-size:44px_44px]" />
        <div className="relative z-10 m-auto max-w-xs">
          <div className="app-icon-tile mx-auto h-10 w-10 text-route">
            <MapPinned className="h-5 w-5" />
          </div>
          <div className="mt-4 text-lg font-semibold tracking-tight">
            {mapError
              ? 'Route map is unavailable'
              : hasAppointments
                ? 'Route is being located'
                : 'No route to show yet'}
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {mapError
              ? 'Your schedule and booking tools still work. Reopen the map from a browser with WebGL enabled.'
              : hasAppointments
                ? 'The map will fill in as appointment addresses are matched.'
                : 'Confirmed jobs with addresses will appear here as today fills in.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full min-h-64 overflow-hidden rounded-[4px] border border-border/70 shadow-[var(--app-shadow-line)]">
      <div ref={containerRef} className="h-full w-full" />
    </div>
  )
}

type RoutePointProperties = {
  kind: 'current' | 'appointment'
  title: string
  detail: string
  secondary: string
  addressText: string
}

const emptyRouteData: FeatureCollection<Point, RoutePointProperties> = {
  type: 'FeatureCollection',
  features: [],
}

function addRouteSourcesAndLayers(map: maplibregl.Map, isDark: boolean) {
  if (!map.isStyleLoaded()) return

  if (!map.getSource('route-points')) {
    map.addSource('route-points', { type: 'geojson', data: emptyRouteData })
  }
  if (!map.getLayer('route-point')) {
    map.addLayer({
      id: 'route-point',
      type: 'circle',
      source: 'route-points',
      paint: {
        'circle-radius': ['case', ['==', ['get', 'kind'], 'current'], 7, 6],
        'circle-color': ['case', ['==', ['get', 'kind'], 'current'], '#4f83b8', '#a66b54'],
        'circle-stroke-color': isDark ? '#17130f' : '#f4f1eb',
        'circle-stroke-width': 2,
      },
    })
  }
  map.setPaintProperty('route-point', 'circle-radius', [
    'case',
    ['==', ['get', 'kind'], 'current'],
    7,
    6,
  ])
  map.setPaintProperty('route-point', 'circle-color', [
    'case',
    ['==', ['get', 'kind'], 'current'],
    '#4f83b8',
    isDark ? '#b87b63' : '#a66b54',
  ])
  map.setPaintProperty('route-point', 'circle-stroke-color', isDark ? '#17130f' : '#f4f1eb')
  map.setPaintProperty('route-point', 'circle-stroke-width', 2)
}

function setRouteSourceData(
  map: maplibregl.Map,
  data: FeatureCollection<Geometry, RoutePointProperties>
) {
  const source = map.getSource('route-points') as GeoJSONSource | undefined
  source?.setData(data)
}

function renderRoutePopup(properties: RoutePointProperties) {
  return `
    <div style="min-width: 160px; color: var(--card-foreground);">
      <div style="font-weight: 600; font-size: 14px;">${escapeMapHtml(properties.title)}</div>
      ${properties.detail ? `<div style="margin-top: 4px; font-size: 13px;">${escapeMapHtml(properties.detail)}</div>` : ''}
      ${properties.secondary ? `<div style="margin-top: 4px; font-size: 13px;">${escapeMapHtml(properties.secondary)}</div>` : ''}
      ${properties.addressText ? `<div style="margin-top: 6px; color: var(--muted-foreground); font-size: 12px; line-height: 1.4;">${escapeMapHtml(properties.addressText)}</div>` : ''}
    </div>
  `
}

function formatDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`
}

function currentTimeKey() {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

export function OperatorOverview({ mode = 'home' }: { mode?: OperatorOverviewMode }) {
  const { configuration, readiness } = useOperatorConfiguration()
  const readinessSummary = useMemo(() => getOperatorReadinessSummary({ readiness }), [readiness])

  const today = useMemo(() => formatDate(new Date()), [])
  const tomorrow = useMemo(() => formatDate(new Date(Date.now() + 86_400_000)), [])
  const dayAfter = useMemo(() => formatDate(new Date(Date.now() + 2 * 86_400_000)), [])

  const { data: todayAppointments = [] } = trpc.appointments.list.useQuery(
    { dateFrom: today, dateTo: today, status: 'confirmed' },
    { enabled: Boolean(configuration.operator) }
  )

  const typedTodayAppointments = todayAppointments as Appointment[]
  const sortedTodayAppointments = useMemo(
    () =>
      [...typedTodayAppointments].sort((left, right) =>
        left.startTime.localeCompare(right.startTime)
      ),
    [typedTodayAppointments]
  )
  const activeServices = configuration.services.filter((service) => service.isActive)
  const previewService = activeServices[0]

  const slotTodayQuery = trpc.booking.getAvailableSlots.useQuery(
    {
      operatorSlug: configuration.operator?.slug ?? '',
      serviceId: previewService?.id ?? '',
      date: today,
    },
    { enabled: Boolean(configuration.operator?.slug && previewService?.id) }
  )

  const slotTomorrowQuery = trpc.booking.getAvailableSlots.useQuery(
    {
      operatorSlug: configuration.operator?.slug ?? '',
      serviceId: previewService?.id ?? '',
      date: tomorrow,
    },
    { enabled: Boolean(configuration.operator?.slug && previewService?.id) }
  )

  const slotDayAfterQuery = trpc.booking.getAvailableSlots.useQuery(
    {
      operatorSlug: configuration.operator?.slug ?? '',
      serviceId: previewService?.id ?? '',
      date: dayAfter,
    },
    { enabled: Boolean(configuration.operator?.slug && previewService?.id) }
  )

  const viewerLocationQuery = trpc.geo.viewerLocation.useQuery()

  const nextTodayAppointment = useMemo(() => {
    const nowKey = currentTimeKey()
    return sortedTodayAppointments.find((appointment) => appointment.startTime >= nowKey) ?? null
  }, [sortedTodayAppointments])

  const routeTravelPairs = useMemo(() => {
    const routed = sortedTodayAppointments.filter((appointment) => appointment.h3Index)
    if (routed.length < 2) return []

    return routed.slice(0, -1).flatMap((appointment, index) => {
      const next = routed[index + 1]
      if (!appointment.h3Index || !next?.h3Index) return []
      return [{ fromH3: appointment.h3Index, toH3: next.h3Index }]
    })
  }, [sortedTodayAppointments])

  const travelTimeQuery = trpc.geo.batchTravelTime.useQuery(
    { pairs: routeTravelPairs },
    { enabled: routeTravelPairs.length > 0 }
  )

  const totalDriveTime = useMemo(
    () => travelTimeQuery.data?.reduce((sum, pair) => sum + (pair?.durationMinutes ?? 0), 0) ?? 0,
    [travelTimeQuery.data]
  )

  const nextOpenWindow = useMemo(() => {
    const candidates = [
      { date: today, slots: slotTodayQuery.data ?? [] },
      { date: tomorrow, slots: slotTomorrowQuery.data ?? [] },
      { date: dayAfter, slots: slotDayAfterQuery.data ?? [] },
    ]

    return candidates.find((candidate) => candidate.slots.length > 0) ?? null
  }, [
    dayAfter,
    slotDayAfterQuery.data,
    slotTodayQuery.data,
    slotTomorrowQuery.data,
    today,
    tomorrow,
  ])

  const artifactPublicationState = configuration.operator?.artifactPublicationState ?? 'draft'
  const attentionItems = [
    ...(readinessSummary.blockingItemCount > 0
      ? [
          {
            label: `${readinessSummary.blockingItemCount} setup blocker${
              readinessSummary.blockingItemCount === 1 ? '' : 's'
            } can affect bookings`,
            href: '/setup' as const,
          },
        ]
      : []),
    ...(artifactPublicationState !== 'public'
      ? [
          {
            label: 'Booking page is private; review appearance when ready',
            href: '/appearance' as const,
          },
        ]
      : []),
    ...(!previewService
      ? [{ label: 'No active service for customer-facing openings', href: '/services' as const }]
      : []),
    ...(previewService && !nextOpenWindow
      ? [
          {
            label: 'No customer-facing openings in the next three days',
            href: '/availability' as const,
          },
        ]
      : []),
    ...(sortedTodayAppointments.some((appointment) => !appointment.addressText)
      ? [{ label: 'A booking is missing an address', href: '/appointments' as const }]
      : []),
  ]

  const liveSignals = [
    {
      value:
        totalDriveTime > 0
          ? `${totalDriveTime} min drive`
          : sortedTodayAppointments.length > 1
            ? '0 min drive'
            : 'No route yet',
      detail:
        sortedTodayAppointments.length > 1
          ? 'between stops'
          : sortedTodayAppointments.length === 1
            ? 'one stop today'
            : 'no confirmed stops',
      icon: Navigation,
    },
    {
      value: artifactPublicationState === 'public' ? 'Booking live' : 'Booking private',
      detail: artifactPublicationState === 'public' ? 'customers can book' : 'not public',
      icon: ShieldCheck,
    },
    {
      value: `${sortedTodayAppointments.length} jobs`,
      detail: 'confirmed today',
      icon: CalendarDays,
    },
    {
      value: attentionItems.length > 0 ? `${attentionItems.length} issue` : 'No issues',
      detail: 'needs attention',
      icon: CheckCircle2,
    },
  ]
  const dayHeadline = nextTodayAppointment
    ? `Next job at ${nextTodayAppointment.startTime}`
    : nextOpenWindow
      ? `${nextOpenWindow.slots.length} bookable openings today`
      : 'Today is open, but not bookable yet'
  const daySummary = nextTodayAppointment
    ? `${nextTodayAppointment.service?.name || 'Service'}${
        nextTodayAppointment.customer?.name ? ` for ${nextTodayAppointment.customer.name}` : ''
      }`
    : nextOpenWindow
      ? `First customer-facing opening is ${nextOpenWindow.slots[0]?.startTime}.`
      : 'Tune availability or service setup to create customer-facing times.'
  const todayBookableSlots = slotTodayQuery.data ?? []
  const tomorrowBookableSlots = slotTomorrowQuery.data ?? []
  const todayTimelineSlots = todayBookableSlots.slice(0, sortedTodayAppointments.length > 0 ? 2 : 4)
  const tomorrowTimelineSlots = tomorrowBookableSlots.slice(0, 5)

  return (
    <AppWorkspacePanel data-tour="dashboard-route">
      <AppWorkspaceHeader>
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-center">
          <div>
            <div className="app-section-label">
              {mode === 'home' ? 'Operations' : 'Operations summary'}
            </div>
            <h2 className="mt-1.5 max-w-3xl font-sans text-xl font-semibold tracking-tight text-foreground md:text-2xl">
              {dayHeadline}
            </h2>
            <p className="mt-1.5 max-w-2xl text-sm text-muted-foreground">{daySummary}</p>
          </div>
          <WorkspaceMetricStrip
            className="sm:grid-cols-2 xl:grid-cols-4"
            metrics={liveSignals.map((signal) => ({
              key: signal.value,
              icon: <signal.icon className="h-3.5 w-3.5" />,
              label: signal.detail,
              value: signal.value,
            }))}
          />
        </div>
      </AppWorkspaceHeader>

      <div className="grid flex-1 divide-y divide-border/70 lg:grid-cols-[1.08fr_0.84fr_0.98fr] lg:divide-x lg:divide-y-0">
        <section className="flex min-h-[340px] flex-col px-5 py-5">
          <div className="app-section-label">Today</div>
          <div className="mt-7 flex flex-1 flex-col">
            <div className="relative flex-1 border-l border-route/45 pl-6">
              {sortedTodayAppointments.length > 0 ? (
                <div className="space-y-5">
                  {sortedTodayAppointments.slice(0, 4).map((appointment) => (
                    <div key={appointment.id} className="relative">
                      <span className="-left-[31px] absolute top-1.5 h-3 w-3 rounded-full border-2 border-route bg-card" />
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="font-sans text-lg font-semibold tracking-tight">
                            {appointment.startTime}
                          </div>
                          <div className="mt-1 text-sm font-medium">
                            {appointment.service?.name || 'Service'}
                          </div>
                          <div className="mt-0.5 text-sm text-muted-foreground">
                            {appointment.customer?.name || 'Customer name pending'}
                          </div>
                          {appointment.addressText ? (
                            <div className="mt-2 flex items-start gap-2 text-xs text-muted-foreground">
                              <MapPinned className="mt-0.5 h-3.5 w-3.5 shrink-0 text-route" />
                              <span>{appointment.addressText}</span>
                            </div>
                          ) : null}
                        </div>
                        {appointment.id === nextTodayAppointment?.id ? (
                          <Badge variant="outline" className="rounded-[4px] font-normal">
                            Next
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="relative">
                  <span className="-left-[33px] absolute top-1 h-4 w-4 rounded-full border-2 border-route bg-card shadow-[0_0_0_5px_rgba(79,131,184,0.08)]" />
                  <div className="text-2xl font-semibold tracking-tight">No confirmed jobs</div>
                  <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                    Today is open for customer-facing bookings.
                  </p>
                </div>
              )}

              {todayTimelineSlots.length > 0 ? (
                <div className="mt-6 space-y-3">
                  {todayTimelineSlots.map((slot) => (
                    <div key={slot.startTime} className="relative">
                      <span className="-left-[30px] absolute top-2 h-2.5 w-2.5 rounded-full border border-route bg-background" />
                      <div className="app-timeline-card flex items-center justify-between gap-3 rounded-[4px] px-3 py-2">
                        <div>
                          <div className="text-sm font-semibold">{slot.startTime}</div>
                          <div className="text-xs text-muted-foreground">Bookable opening</div>
                        </div>
                        <Clock3 className="h-4 w-4 text-route" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Button asChild className="cursor-pointer">
                <Link to="/availability">
                  Review openings
                  <MoveRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="cursor-pointer">
                <Link to="/appointments">
                  Add booking
                  <Plus className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="flex min-h-[340px] flex-col px-5 py-5">
          <div className="app-section-label">Tomorrow</div>
          {tomorrowTimelineSlots.length > 0 ? (
            <div className="mt-7 flex flex-1 flex-col">
              <div className="relative flex-1 border-l border-border/80 pl-6">
                <div className="space-y-3">
                  {tomorrowTimelineSlots.map((slot) => (
                    <div key={slot.startTime} className="relative">
                      <span className="-left-[30px] absolute top-3 h-2.5 w-2.5 rounded-full border border-route/70 bg-card" />
                      <div className="app-timeline-card rounded-[4px] px-3 py-3">
                        <div className="text-sm font-semibold">{slot.startTime}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">Bookable opening</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Button asChild variant="ghost" className="mt-5 h-auto justify-start px-0 text-route">
                <Link to="/availability">
                  Tune tomorrow
                  <MoveRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-center">
              <div className="app-icon-tile h-20 w-20 text-route">
                <Navigation className="h-6 w-6" />
              </div>
              <div className="mt-6 text-xl font-semibold tracking-tight">No openings tomorrow</div>
              <p className="mt-2 max-w-xs text-sm text-muted-foreground">
                Add hours or adjust notice rules to create bookable times.
              </p>
            </div>
          )}
        </section>

        <section className="grid min-h-[340px] grid-rows-[1fr_auto] divide-y divide-border/70">
          <div className="px-5 py-5" data-tour="open-slots">
            <div className="app-section-label">Route map</div>
            <div className="mt-7 h-[19.25rem]">
              <DashboardRouteMap
                appointments={sortedTodayAppointments}
                viewerLocation={viewerLocationQuery.data ?? null}
              />
            </div>
            <div className="app-data-card mt-4 grid grid-cols-2 divide-x divide-border/70 overflow-hidden rounded-[4px]">
              <div className="px-3 py-3">
                <div className="app-section-label">Stops</div>
                <div className="mt-1 text-sm font-medium">{sortedTodayAppointments.length}</div>
              </div>
              <div className="px-3 py-3">
                <div className="app-section-label">Current location</div>
                <div className="mt-1 truncate text-sm font-medium">
                  {viewerLocationQuery.data
                    ? [viewerLocationQuery.data.city, viewerLocationQuery.data.region]
                        .filter(Boolean)
                        .join(', ') || 'Available'
                    : 'Unavailable'}
                </div>
              </div>
            </div>
          </div>

          <div className="px-5 py-5">
            <div className="app-section-label">Needs attention</div>
            {attentionItems.length > 0 ? (
              <div className="mt-5 divide-y divide-border/70 overflow-hidden rounded-[4px] border border-border/70 bg-background/42">
                {attentionItems.slice(0, 2).map((item) => (
                  <Link
                    key={item.label}
                    to={item.href}
                    className="flex items-center justify-between gap-3 px-3 py-3 text-sm transition-colors hover:bg-primary/[0.045] hover:text-primary"
                  >
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-warning" />
                      {item.label}
                    </span>
                    <MoveRight className="h-4 w-4 shrink-0" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="mt-5 flex items-center gap-2 border-y border-border/70 py-3 text-sm">
                <CheckCircle2 className="h-4 w-4 text-success" />
                No issues affecting today&apos;s bookings.
              </div>
            )}
          </div>
        </section>
      </div>
    </AppWorkspacePanel>
  )
}
